// src/components/sdk/components/ClientLogContent.tsx
import React from 'react';
import {
    Layout,
    Table,
    Select,
    Button,
    Typography,
    Space,
    Row,
    Col,
    Card,
    Tag,
    Pagination,
    message,
    Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    FileTextOutlined,
    ReloadOutlined,
    DownloadOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { Global } from '../../../services/Global';
import {
    DEFAULT_RPP,
    getSourceName,
} from '../../../services/Macro';
import { browserUtil } from '../../../utils/BrowserUtil';
import { mathUtil } from '../../../utils/MathUtil';
import {
    ClientLogData,
    ClientLogContentProps,
    ClientLogContentState,
} from '../SdkInterface';

const { Content } = Layout;
const { Title, Text } = Typography;

/** 日志文件下载前缀 */
const SDK_LOG_DOWNLOAD_BASE = 'https://ctrl.healthassistant.app/files/sdk_logs/';

/** 日志项，直接对应服务端 ClientLogResponse */
export interface LogItem {
    id: number | string;
    packageName: string;
    userName: string;
    logDate: string;
    logSize: number;
    updateTime: number;
    source: number;
}

/** 日志页只需要的 props */
interface ClientLogContentProps2 extends ClientLogContentProps {
    isMobile?: boolean;
    /** 删除单条日志的接口方法，返回 Promise */
    onDeleteLog?: (id: number | string) => Promise<void>;
}

interface ClientLogContentState2 extends ClientLogContentState {
    logsPerPage: number;
    logsCurrentPage: number;
    detailVisible: boolean;
    detailId: number | string | null;
    /** 正在下载的文件名，用于按钮 loading */
    downloadingFile: string | null;
    /** 正在删除的日志 id，用于按钮 loading */
    deletingId: number | string | null;
}

export class ClientLogContent extends React.Component<
    ClientLogContentProps2,
    ClientLogContentState2
> {
    constructor(props: ClientLogContentProps2) {
        super(props);
        this.state = {
            logsPerPage: props.clientLogsPageSize || 10,
            logsCurrentPage: props.clientLogsPage || 1,
            detailVisible: false,
            detailId: null,
            downloadingFile: null,
            deletingId: null,
        };
    }

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile();
    }

    onLppChange = (value: number) => {
        const oldLogsPerPage = this.state.logsPerPage;
        this.setState({ logsPerPage: value }, () => {
            this.setState(
                {
                    logsCurrentPage: mathUtil.getNewPage(
                        oldLogsPerPage,
                        this.state.logsCurrentPage,
                        this.state.logsPerPage
                    ),
                },
                () => this.updateLogList()
            );
        });
    };

    updateLogList = () => {
        this.props.onPageChange(this.state.logsCurrentPage, this.state.logsPerPage);
    };

    onLogPageChange = (page: number) => {
        this.setState({ logsCurrentPage: page }, () => this.updateLogList());
    };

    refresh = () => {
        this.props.onRefresh();
    };

    showLogDetail = (id: number | string) => {
        this.setState({ detailVisible: true, detailId: id });
    };

    closeLogDetail = () => {
        this.setState({ detailVisible: false, detailId: null });
    };

    getLogList = (): LogItem[] => {
        const { clientLogs } = this.props;
        return (clientLogs || []).map((log: ClientLogData) => ({
            id: log.id,
            packageName: log.packageName,
            userName: log.userName,
            logDate: log.logDate,
            logSize: log.logSize,
            updateTime: log.updateTime,
            source: log.source,
        }));
    };

    /** 拼接日志文件名：{userName}_{source}_{logDate}.log */
    getLogFileName = (item: LogItem): string => {
        return `${item.userName}_${item.source}_${item.logDate}.log`;
    };

    /** 拼接下载链接 */
    getLogFileUrl = (item: LogItem): string => {
        return `${SDK_LOG_DOWNLOAD_BASE}${this.getLogFileName(item)}`;
    };

    /**
     * 直接下载文件：
     * 用 fetch 拉取 blob，再触发 <a download> 下载，避免跨域时 download 属性失效、
     * 变成「打开新网页」的行为。
     */
    handleDownload = async (item: LogItem) => {
        const fileName = this.getLogFileName(item);
        const url = this.getLogFileUrl(item);

        this.setState({ downloadingFile: fileName });

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // 释放 blob URL
            window.URL.revokeObjectURL(blobUrl);

            message.success(`下载成功：${fileName}`);
        } catch (e) {
            console.error('下载日志文件失败:', e);
            message.error(this.props.t('sdk.log.downloadFailed') || '下载失败');
        } finally {
            this.setState({ downloadingFile: null });
        }
    };

    /**
     * 删除单条日志。
     * 由父组件通过 onDeleteLog 传入实际接口调用。
     */
    handleDelete = async (item: LogItem) => {
        const t = this.props.t;
        const { onDeleteLog } = this.props;

        if (!onDeleteLog) {
            message.error(t('sdk.log.deleteFailed') || '删除失败：未提供删除接口');
            return;
        }

        this.setState({ deletingId: item.id });

        try {
            await onDeleteLog(item.id);
            message.success(t('sdk.log.deleteSuccess') || '删除成功');
            // 删除成功后重新拉取当前页数据
            this.updateLogList();
        } catch (e) {
            console.error('删除日志失败:', e);
            message.error(t('sdk.log.deleteFailed') || '删除失败');
        } finally {
            this.setState({ deletingId: null });
        }
    };

    formatSize = (size: number): string => {
        if (size == null) return '-';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
        return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
    };

    formatUpdateTime = (ts: number): string => {
        if (ts == null) return '-';
        const d = new Date(ts);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    getPcColumns = (): ColumnsType<LogItem> => {
        const t = this.props.t;
        return [
            {
                title: t('sdk.log.date') || '日期',
                dataIndex: 'logDate',
                key: 'logDate',
                width: 80,
                render: (text: string) => (
                    <Space size={4}>
                        <FileTextOutlined style={{ color: '#8c8c8c' }} />
                        <Text>{text}</Text>
                    </Space>
                ),
            },
            {
                title: t('sdk.log.pkgName') || '包名',
                dataIndex: 'packageName',
                key: 'packageName',
                width: 120,
                ellipsis: true,
            },
            {
                title: t('sdk.log.uName') || '用户名',
                dataIndex: 'userName',
                key: 'userName',
                width: 160,
                ellipsis: true,
            },
            {
                title: t('size') || '大小',
                dataIndex: 'logSize',
                key: 'logSize',
                width: 70,
                render: (size: number) => <Text type="secondary">{this.formatSize(size)}</Text>,
            },
            {
                title: t('updateTime') || '更新时间',
                dataIndex: 'updateTime',
                key: 'updateTime',
                width: 100,
                render: (ts: number) => <Text type="secondary">{this.formatUpdateTime(ts)}</Text>,
            },
            {
                title: t('source') || '来源',
                dataIndex: 'source',
                key: 'source',
                width: 50,
                render: (source: number) => <Tag color="blue">{getSourceName(source)}</Tag>,
            },
            {
                title: t('download') || '下载',
                key: 'download',
                width: 280,
                render: (_: any, record: LogItem) => {
                    const fileName = this.getLogFileName(record);
                    const isLoading = this.state.downloadingFile === fileName;
                    return (
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            icon={<DownloadOutlined />}
                            loading={isLoading}
                            onClick={() => this.handleDownload(record)}
                        >
                            <Text
                                style={{ maxWidth: 320, display: 'inline-block' }}
                                ellipsis
                            >
                                {fileName}
                            </Text>
                        </Button>
                    );
                },
            },
            {
                title: t('action') || '操作',
                key: 'action',
                width: 80,
                fixed: 'right',
                render: (_: any, record: LogItem) => (
                    <Popconfirm
                        title={t('sdk.log.deleteConfirmTitle') || '确认删除'}
                        description={
                            t('sdk.log.deleteConfirmDesc') ||
                            '删除后无法恢复，确定要删除这条日志吗？'
                        }
                        okText={t('confirm') || '确定'}
                        cancelText={t('cancel') || '取消'}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => this.handleDelete(record)}
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            style={{ padding: 0 }}
                            icon={<DeleteOutlined />}
                            loading={this.state.deletingId === record.id}
                        >
                            {t('delete') || '删除'}
                        </Button>
                    </Popconfirm>
                ),
            },
        ];
    };

    getMobileColumns = (): ColumnsType<LogItem> => {
        const t = this.props.t;
        return [
            {
                title: '',
                key: 'detail',
                width: 40,
                render: (_: any, record: LogItem) => (
                    <Button
                        type="link"
                        size="small"
                        icon={<span style={{ fontSize: 14 }}>▾</span>}
                        onClick={() => this.showLogDetail(record.id)}
                    />
                ),
            },
            {
                title: t('logDate') || '日期',
                dataIndex: 'logDate',
                key: 'logDate',
                width: 100,
            },
            {
                title: t('packageName') || '包名',
                dataIndex: 'packageName',
                key: 'packageName',
                ellipsis: true,
            },
            {
                title: t('userName') || '用户名',
                dataIndex: 'userName',
                key: 'userName',
                width: 120,
                ellipsis: true,
            },
            {
                title: t('size') || '大小',
                dataIndex: 'logSize',
                key: 'logSize',
                width: 80,
                render: (size: number) => this.formatSize(size),
            },
            {
                title: t('download') || '下载',
                key: 'download',
                width: 50,
                render: (_: any, record: LogItem) => {
                    const fileName = this.getLogFileName(record);
                    const isLoading = this.state.downloadingFile === fileName;
                    return (
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            icon={<DownloadOutlined />}
                            loading={isLoading}
                            onClick={() => this.handleDownload(record)}
                        />
                    );
                },
            },
            {
                title: t('action') || '操作',
                key: 'action',
                width: 50,
                render: (_: any, record: LogItem) => (
                    <Popconfirm
                        title={t('sdk.log.deleteConfirmTitle') || '确认删除'}
                        description={
                            t('sdk.log.deleteConfirmDesc') ||
                            '删除后无法恢复，确定要删除这条日志吗？'
                        }
                        okText={t('confirm') || '确定'}
                        cancelText={t('cancel') || '取消'}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => this.handleDelete(record)}
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            style={{ padding: 0 }}
                            icon={<DeleteOutlined />}
                            loading={this.state.deletingId === record.id}
                        />
                    </Popconfirm>
                ),
            },
        ];
    };

    render() {
        if (this.props.isMobile) return this.renderMobile();
        return this.renderPc();
    }

    renderMobile() {
        const t = this.props.t;
        const { logsPerPage, logsCurrentPage } = this.state;
        const list = this.getLogList();
        const logCurP = this.props.clientLogsPage ?? logsCurrentPage;
        const logTotalP = this.props.clientLogsTotalPages ?? 0;
        const logTotalR = this.props.clientLogsTotal ?? 0;

        return (
            <Content style={{ margin: '4px', padding: '6px', background: 'var(--content-bg)', minHeight: '280px', display: 'flex', flexDirection: 'column' }}>
                <Card
                    title={
                        <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('logsMgmt') || '日志管理'}</Title>
                        </Space>
                    }
                    extra={<Button icon={<ReloadOutlined />} onClick={this.refresh}>{t('refresh') || '刷新'}</Button>}
                    style={{ borderRadius: 8, display: 'flex', flexDirection: 'column', flex: 1, background: 'var(--card-bg)' }}
                    bodyStyle={{ padding: '6px', display: 'flex', flexDirection: 'column', flex: 1 }}
                >
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Table<LogItem>
                            rowKey="id"
                            columns={this.getMobileColumns()}
                            dataSource={list}
                            pagination={false}
                            size="small"
                            bordered
                            loading={this.props.clientLogsLoading}
                        />
                    </div>
                    <Row justify="space-between" align="middle" style={{ marginTop: 16, flexShrink: 0, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                        <Col>
                            <Text type="secondary">
                                {t('table.footer.totalRecords') || '共'} {logTotalR} {t('table.footer.records') || '条记录'}
                                ，{t('table.footer.currentPage') || '当前第'} {logCurP}/{logTotalP} {t('table.footer.page') || ''}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>{t('table.footer.pageSize') || '每页'}</span>
                                <Select value={logsPerPage} onChange={this.onLppChange} style={{ width: 80 }} options={DEFAULT_RPP.map((item: number) => ({ label: item, value: item }))} />
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>{t('items') || '条'}</span>
                                <Pagination current={logCurP} total={logTotalR} pageSize={logsPerPage} onChange={this.onLogPageChange} showSizeChanger={false} simple size="small" />
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Content>
        );
    }

    renderPc() {
        const t = this.props.t;
        const { logsPerPage, logsCurrentPage } = this.state;
        const list = this.getLogList();
        const logCurP = this.props.clientLogsPage ?? logsCurrentPage;
        const logTotalP = this.props.clientLogsTotalPages ?? 0;
        const logTotalR = this.props.clientLogsTotal ?? 0;

        const tableScrollY = window.innerHeight - 330;
        const rowHeight = 56;
        const headerHeight = 38;
        const availableHeight = tableScrollY - headerHeight;
        const totalContentHeight = list.length * rowHeight;
        const needScroll = totalContentHeight > availableHeight;

        return (
            <Content style={{ margin: '4px', padding: '6px', background: 'var(--content-bg)', minHeight: '280px', display: 'flex', flexDirection: 'column' }}>
                <Card
                    title={
                        <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('logsMgmt') || '日志管理'}</Title>
                            <Tag color="blue">{logTotalR} {t('records') || '条记录'}</Tag>
                        </Space>
                    }
                    extra={<Button icon={<ReloadOutlined />} onClick={this.refresh}>{t('refresh') || '刷新'}</Button>}
                    style={{ borderRadius: 8, display: 'flex', flexDirection: 'column', flex: 1, background: 'var(--card-bg)' }}
                    bodyStyle={{ padding: '6px', display: 'flex', flexDirection: 'column', flex: 1 }}
                >
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Table<LogItem>
                            rowKey="id"
                            columns={this.getPcColumns()}
                            dataSource={list}
                            pagination={false}
                            bordered
                            size="small"
                            loading={this.props.clientLogsLoading}
                            style={{ borderRadius: 6, height: '100%' }}
                            scroll={{ x: 1030, y: needScroll ? (tableScrollY > 200 ? tableScrollY : 300) : undefined }}
                        />
                    </div>
                    <Row justify="space-between" align="middle" style={{ marginTop: 16, flexShrink: 0, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                        <Col>
                            <Text type="secondary">
                                {t('table.footer.totalRecords') || '共'} {logTotalR} {t('table.footer.records') || '条记录'}
                                ，{t('table.footer.currentPage') || '当前第'} {logCurP}/{logTotalP} {t('table.footer.page') || ''}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>{t('table.footer.pageSize') || '每页'}</span>
                                <Select value={logsPerPage} onChange={this.onLppChange} style={{ width: 80 }} options={DEFAULT_RPP.map((item: number) => ({ label: item, value: item }))} />
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>{t('table.footer.items') || '条'}</span>
                                <Pagination
                                    current={logCurP}
                                    total={logTotalR}
                                    pageSize={logsPerPage}
                                    onChange={this.onLogPageChange}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    locale={{
                                        items_per_page: t('table.footer.itemsPerPage') || '条/页',
                                        jump_to: t('table.footer.jumpTo') || '跳至',
                                        jump_to_confirm: t('table.footer.confirm') || '确定',
                                        page: t('table.footer.page') || '',
                                        prev_page: t('table.footer.prevPage') || '上一页',
                                        next_page: t('table.footer.nextPage') || '下一页',
                                        prev_5: t('table.footer.prev5') || '向前 5 页',
                                        next_5: t('table.footer.next5') || '向后 5 页',
                                        prev_3: t('table.footer.prev3') || '向前 3 页',
                                        next_3: t('table.footer.next3') || '向后 3 页',
                                    }}
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Content>
        );
    }
}

export default ClientLogContent;